import React, { useEffect, useState } from 'react';
import {
  Layout, Card, Button, Table, Modal, Form, Input, DatePicker, Select,
  Tag, Space, Typography, message, Row, Col, Tooltip, Popconfirm, Switch, Dropdown
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, CalendarOutlined,
  CheckCircleOutlined, ClockCircleOutlined, RocketOutlined, ReloadOutlined,
  DownOutlined, SyncOutlined, PlayCircleOutlined, ScheduleOutlined,
  FieldTimeOutlined, FileTextOutlined, SafetyCertificateOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import Sidebar from './Sidebar';
import MainHeader from './MainHeader';
import api from '../api';

const { Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;

const STATUS_COLOR_MAP = {
  Draft: 'default',
  Scheduled: 'blue',
  Active: 'processing',
  Open: 'processing',
  'Self Assessment': 'cyan',
  'Manager Review': 'warning',
  'HR Review': 'purple',
  'Approval Pending': 'gold',
  Completed: 'success',
  Closed: 'magenta',
};

export default function PmsCycleManagement() {
  const [collapsed, setCollapsed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cycles, setCycles] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCycle, setEditingCycle] = useState(null);
  const [form] = Form.useForm();

  const fetchCycles = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/pms/cycles');
      if (res.data?.success) {
        setCycles(res.data.cycles || []);
      } else {
        message.error(res.data?.message || 'Failed to load appraisal cycles');
      }
    } catch (e) {
      console.error('Fetch appraisal cycles error:', e);
      message.error(e.response?.data?.message || 'Failed to load appraisal cycles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCycles();
  }, []);

  const parseFinancialYear = (fyStr) => {
    if (!fyStr) return dayjs();
    const startYr = parseInt(String(fyStr).split('-')[0]);
    if (!isNaN(startYr) && startYr > 1900 && startYr < 2100) {
      return dayjs(`${startYr}-01-01`);
    }
    return dayjs();
  };

  const handleTransitionStatus = async (id, action, targetStatus) => {
    try {
      setLoading(true);
      const res = await api.post(`/admin/pms/cycles/${id}/transition`, { action, targetStatus });
      if (res.data?.success) {
        message.success(res.data?.message || 'Cycle status updated successfully');
        fetchCycles();
      } else {
        message.error(res.data?.message || 'Failed to update status');
      }
    } catch (e) {
      console.error('Transition status error:', e);
      message.error(e.response?.data?.message || 'Failed to transition status');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (cycle = null) => {
    setEditingCycle(cycle);
    if (cycle) {
      form.setFieldsValue({
        cycleName: cycle.cycleName,
        financialYear: parseFinancialYear(cycle.financialYear),
        frequency: cycle.frequency || 'Annual',
        frequencyPeriod: cycle.frequencyPeriod || '',
        allowHrExtension: cycle.allowHrExtension !== undefined ? cycle.allowHrExtension : false,
        startDate: cycle.startDate ? dayjs(cycle.startDate) : null,
        endDate: cycle.endDate ? dayjs(cycle.endDate) : null,
        appraisalStartDate: cycle.appraisalStartDate ? dayjs(cycle.appraisalStartDate) : null,
        selfAppraisalDeadline: cycle.selfAppraisalDeadline ? dayjs(cycle.selfAppraisalDeadline) : null,
        managerReviewDeadline: cycle.managerReviewDeadline ? dayjs(cycle.managerReviewDeadline) : null,
        hrReviewDeadline: cycle.hrReviewDeadline ? dayjs(cycle.hrReviewDeadline) : null,
        managementApprovalDeadline: (cycle.managementApprovalDeadline || cycle.finalApprovalDeadline) ? dayjs(cycle.managementApprovalDeadline || cycle.finalApprovalDeadline) : null,
        salaryRevisionEffectiveDate: cycle.salaryRevisionEffectiveDate ? dayjs(cycle.salaryRevisionEffectiveDate) : null,
        status: cycle.status || 'Draft',
        description: cycle.description || '',
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        financialYear: dayjs(),
        frequency: 'Annual',
        frequencyPeriod: '',
        allowHrExtension: false,
        status: 'Auto',
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCycle(null);
    form.resetFields();
  };

  const handleSubmit = async (values) => {
    try {
      let financialYearStr = '';
      if (values.financialYear && dayjs.isDayjs(values.financialYear) && values.financialYear.isValid()) {
        financialYearStr = `${values.financialYear.year()}-${values.financialYear.year() + 1}`;
      } else if (values.financialYear) {
        financialYearStr = String(values.financialYear);
      }

      const payload = {
        cycleName: values.cycleName,
        financialYear: financialYearStr,
        frequency: values.frequency || 'Annual',
        frequencyPeriod: values.frequencyPeriod || null,
        allowHrExtension: !!values.allowHrExtension,
        startDate: values.startDate ? values.startDate.format('YYYY-MM-DD') : null,
        endDate: values.endDate ? values.endDate.format('YYYY-MM-DD') : null,
        appraisalStartDate: values.appraisalStartDate ? values.appraisalStartDate.format('YYYY-MM-DD') : null,
        selfAppraisalDeadline: values.selfAppraisalDeadline ? values.selfAppraisalDeadline.format('YYYY-MM-DD') : null,
        managerReviewDeadline: values.managerReviewDeadline ? values.managerReviewDeadline.format('YYYY-MM-DD') : null,
        hrReviewDeadline: values.hrReviewDeadline ? values.hrReviewDeadline.format('YYYY-MM-DD') : null,
        managementApprovalDeadline: values.managementApprovalDeadline ? values.managementApprovalDeadline.format('YYYY-MM-DD') : null,
        finalApprovalDeadline: values.managementApprovalDeadline ? values.managementApprovalDeadline.format('YYYY-MM-DD') : null,
        salaryRevisionEffectiveDate: values.salaryRevisionEffectiveDate ? values.salaryRevisionEffectiveDate.format('YYYY-MM-DD') : null,
        status: values.status,
        description: values.description || null,
      };

      if (editingCycle) {
        const res = await api.put(`/admin/pms/cycles/${editingCycle.id}`, payload);
        if (res.data?.success) {
          message.success('Appraisal Cycle updated successfully');
          handleCloseModal();
          fetchCycles();
        } else {
          message.error(res.data?.message || 'Failed to update cycle');
        }
      } else {
        const res = await api.post('/admin/pms/cycles', payload);
        if (res.data?.success) {
          message.success('Appraisal Cycle created successfully');
          handleCloseModal();
          fetchCycles();
        } else {
          message.error(res.data?.message || 'Failed to create cycle');
        }
      }
    } catch (e) {
      console.error('Save appraisal cycle error:', e);
      message.error(e.response?.data?.message || 'Failed to save appraisal cycle');
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await api.delete(`/admin/pms/cycles/${id}`);
      if (res.data?.success) {
        message.success('Appraisal Cycle deleted successfully');
        fetchCycles();
      } else {
        message.error(res.data?.message || 'Failed to delete cycle');
      }
    } catch (e) {
      console.error('Delete appraisal cycle error:', e);
      message.error(e.response?.data?.message || 'Failed to delete cycle');
    }
  };

  const columns = [
    {
      title: 'Cycle Name',
      dataIndex: 'cycleName',
      key: 'cycleName',
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: '700', color: '#1e293b', fontSize: '14px' }}>{text}</div>
          <div style={{ fontSize: '11px', color: '#64748b' }}>FY: {record.financialYear}</div>
        </div>
      ),
    },
    {
      title: 'Frequency',
      key: 'frequency',
      render: (_, record) => (
        <div>
          <Tag color="blue" style={{ borderRadius: '10px', fontWeight: '600' }}>
            {record.frequency || 'Annual'}
          </Tag>
          {record.frequencyPeriod && (
            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px', fontWeight: '500' }}>
              {record.frequencyPeriod}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Performance Period',
      key: 'period',
      render: (_, record) => (
        <span style={{ fontSize: '12px' }}>
          <CalendarOutlined style={{ marginRight: '4px', color: '#1677ff' }} />
          {record.startDate ? dayjs(record.startDate).format('DD MMM YYYY') : 'N/A'} - {' '}
          {record.endDate ? dayjs(record.endDate).format('DD MMM YYYY') : 'N/A'}
        </span>
      ),
    },
    {
      title: 'Appraisal Deadlines',
      key: 'deadlines',
      render: (_, record) => {
        const mgmtDeadline = record.managementApprovalDeadline || record.finalApprovalDeadline;
        return (
          <div style={{ fontSize: '11px', lineHeight: '1.6' }}>
            {record.selfAppraisalDeadline && (
              <div><Text type="secondary">Self:</Text> <span style={{ fontWeight: 500 }}>{dayjs(record.selfAppraisalDeadline).format('DD MMM YYYY')}</span></div>
            )}
            {record.managerReviewDeadline && (
              <div><Text type="secondary">Manager:</Text> <span style={{ fontWeight: 500 }}>{dayjs(record.managerReviewDeadline).format('DD MMM YYYY')}</span></div>
            )}
            {record.hrReviewDeadline && (
              <div><Text type="secondary">HR:</Text> <span style={{ fontWeight: 500 }}>{dayjs(record.hrReviewDeadline).format('DD MMM YYYY')}</span></div>
            )}
            {mgmtDeadline && (
              <div><Text type="secondary">Management:</Text> <span style={{ fontWeight: 500 }}>{dayjs(mgmtDeadline).format('DD MMM YYYY')}</span></div>
            )}
            {!record.selfAppraisalDeadline && !record.managerReviewDeadline && !record.hrReviewDeadline && !mgmtDeadline && (
              <span style={{ color: '#94a3b8' }}>-</span>
            )}
          </div>
        );
      },
    },
    {
      title: 'Salary Revision Effective',
      dataIndex: 'salaryRevisionEffectiveDate',
      key: 'salaryRevisionEffectiveDate',
      render: (date) => (
        <span style={{ fontSize: '12px', fontWeight: date ? 600 : 400, color: date ? '#0f766e' : '#64748b' }}>
          {date ? dayjs(date).format('DD MMM YYYY') : '-'}
        </span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => {
        const menuItems = [
          {
            key: 'header',
            label: <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '600' }}>AUTHORIZED OVERRIDE</span>,
            disabled: true,
          },
          {
            key: 'draft',
            label: 'Move to Draft',
            disabled: status === 'Draft',
            onClick: () => handleTransitionStatus(record.id, 'revert_draft'),
          },
          {
            key: 'schedule',
            label: 'Move to Scheduled',
            disabled: status === 'Scheduled',
            onClick: () => handleTransitionStatus(record.id, 'schedule'),
          },
          {
            key: 'activate',
            label: 'Activate Cycle (Active)',
            disabled: status === 'Active' || status === 'Open',
            onClick: () => handleTransitionStatus(record.id, 'activate'),
          },
          {
            key: 'complete',
            label: 'Mark as Completed',
            disabled: status === 'Completed' || status === 'Closed',
            onClick: () => handleTransitionStatus(record.id, 'complete'),
          },
          {
            type: 'divider',
          },
          {
            key: 'auto',
            label: 'Re-sync from Dates',
            icon: <SyncOutlined style={{ color: '#1677ff' }} />,
            onClick: () => handleTransitionStatus(record.id, 'auto'),
          },
        ];

        return (
          <Tooltip title="Status is date-driven and updates automatically. Click to manually override.">
            <Dropdown menu={{ items: menuItems }} trigger={['click']}>
              <Button
                size="small"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '2px 8px',
                  height: 'auto',
                  borderRadius: '12px',
                  cursor: 'pointer'
                }}
              >
                <Tag
                  color={STATUS_COLOR_MAP[status] || 'default'}
                  style={{ margin: 0, borderRadius: '8px', fontWeight: '600' }}
                >
                  {status}
                </Tag>
                <DownOutlined style={{ fontSize: '10px', color: '#64748b' }} />
              </Button>
            </Dropdown>
          </Tooltip>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Edit Cycle">
            <Button
              type="text"
              icon={<EditOutlined style={{ color: '#1677ff' }} />}
              onClick={() => handleOpenModal(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete Appraisal Cycle?"
            description="Are you sure you want to delete this cycle?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete Cycle">
              <Button type="text" icon={<DeleteOutlined style={{ color: '#ff4d4f' }} />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sidebar collapsed={collapsed} />
      <Layout style={{ marginLeft: collapsed ? 80 : 200, height: '100vh', overflow: 'hidden', transition: 'margin-left 0.2s' }}>
        <MainHeader collapsed={collapsed} setCollapsed={setCollapsed} title="Performance Management - Appraisal Cycles" />
        <Content style={{ margin: '24px 16px', padding: 24, background: '#f5f5f5', height: 'calc(100vh - 64px - 48px)', overflow: 'auto' }}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            
            {/* KPI Cards Header */}
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={6}>
                <Card bodyStyle={{ padding: '16px' }} style={{ borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                  <div style={{ color: '#64748b', fontSize: '12px', fontWeight: '600' }}>TOTAL CYCLES</div>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#1e293b', marginTop: '4px' }}>{cycles.length}</div>
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card bodyStyle={{ padding: '16px' }} style={{ borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                  <div style={{ color: '#64748b', fontSize: '12px', fontWeight: '600' }}>SCHEDULED CYCLES</div>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#1677ff', marginTop: '4px' }}>
                    {cycles.filter(c => c.status === 'Scheduled').length}
                  </div>
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card bodyStyle={{ padding: '16px' }} style={{ borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                  <div style={{ color: '#64748b', fontSize: '12px', fontWeight: '600' }}>ACTIVE CYCLES</div>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#13c2c2', marginTop: '4px' }}>
                    {cycles.filter(c => ['Active', 'Open', 'Self Assessment', 'Manager Review', 'HR Review', 'Approval Pending'].includes(c.status)).length}
                  </div>
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card bodyStyle={{ padding: '16px' }} style={{ borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                  <div style={{ color: '#64748b', fontSize: '12px', fontWeight: '600' }}>COMPLETED / CLOSED</div>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#52c41a', marginTop: '4px' }}>
                    {cycles.filter(c => ['Completed', 'Closed'].includes(c.status)).length}
                  </div>
                </Card>
              </Col>
            </Row>

            {/* Main Content Card */}
            <Card
              title={<span style={{ fontWeight: '700', fontSize: '16px', color: '#1e293b' }}>Appraisal Cycles Registry</span>}
              extra={
                <Space>
                  <Button icon={<ReloadOutlined />} onClick={fetchCycles}>Refresh</Button>
                  <Button type="primary" icon={<PlusOutlined />} shape="round" onClick={() => handleOpenModal()}>
                    New Appraisal Cycle
                  </Button>
                </Space>
              }
              style={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}
            >
              <Table
                columns={columns}
                dataSource={cycles}
                rowKey="id"
                loading={loading}
                pagination={{ pageSize: 10 }}
              />
            </Card>

          </Space>
        </Content>
      </Layout>

      {/* Create / Edit Modal - Upgraded to 880px Spacious Card Design */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: 4, height: 22, background: '#1677ff', borderRadius: 2 }} />
            <span style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b' }}>
              {editingCycle ? 'Edit Appraisal Cycle' : 'Create New Appraisal Cycle'}
            </span>
          </div>
        }
        open={isModalOpen}
        onCancel={handleCloseModal}
        footer={null}
        width={880}
        destroyOnClose
        style={{ top: 20 }}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} style={{ marginTop: '16px' }}>
          
          {/* Card 1: Cycle Identity & Fiscal Period */}
          <Card
            type="inner"
            title={
              <Space>
                <ScheduleOutlined style={{ color: '#1677ff' }} />
                <span style={{ fontWeight: 600, color: '#334155' }}>Cycle Identity & Fiscal Period</span>
              </Space>
            }
            style={{ marginBottom: 16, borderRadius: 10, borderColor: '#e2e8f0', background: '#fafbfc' }}
            bodyStyle={{ padding: '16px 20px' }}
          >
            <Row gutter={16}>
              <Col span={15}>
                <Form.Item
                  name="cycleName"
                  label={<strong>Cycle Name</strong>}
                  rules={[{ required: true, message: 'Enter Cycle Name (e.g., 2026-27 Annual Appraisal)' }]}
                >
                  <Input placeholder="e.g. 2026-27 Annual Appraisal" />
                </Form.Item>
              </Col>
              <Col span={9}>
                <Form.Item
                  name="financialYear"
                  label={<strong>Financial Year</strong>}
                  rules={[{ required: true, message: 'Select Financial Year' }]}
                >
                  <DatePicker
                    picker="year"
                    style={{ width: '100%' }}
                    placeholder="Select Year"
                    format={(val) => (val && val.isValid() ? `${val.year()}-${val.year() + 1}` : '')}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="frequency" label={<strong>Appraisal Frequency</strong>} rules={[{ required: true }]}>
                  <Select onChange={() => form.setFieldsValue({ frequencyPeriod: undefined })}>
                    <Option value="Annual">Annual</Option>
                    <Option value="Half-Yearly">Half-Yearly</Option>
                    <Option value="Quarterly">Quarterly</Option>
                    <Option value="Monthly">Monthly</Option>
                    <Option value="Custom">Custom</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, currentValues) => prevValues.frequency !== currentValues.frequency}
                >
                  {({ getFieldValue }) => {
                    const freq = getFieldValue('frequency');
                    if (freq === 'Quarterly') {
                      return (
                        <Form.Item name="frequencyPeriod" label={<strong>Quarter</strong>}>
                          <Select placeholder="Select Quarter (Q1, Q2, Q3, Q4)">
                            <Option value="Q1">Q1 (Apr - Jun)</Option>
                            <Option value="Q2">Q2 (Jul - Sep)</Option>
                            <Option value="Q3">Q3 (Oct - Dec)</Option>
                            <Option value="Q4">Q4 (Jan - Mar)</Option>
                          </Select>
                        </Form.Item>
                      );
                    }
                    if (freq === 'Half-Yearly') {
                      return (
                        <Form.Item name="frequencyPeriod" label={<strong>Half-Year Period</strong>}>
                          <Select placeholder="Select Half-Year (H1, H2)">
                            <Option value="H1">H1 (Apr - Sep)</Option>
                            <Option value="H2">H2 (Oct - Mar)</Option>
                          </Select>
                        </Form.Item>
                      );
                    }
                    if (freq === 'Monthly') {
                      return (
                        <Form.Item name="frequencyPeriod" label={<strong>Month</strong>}>
                          <Select placeholder="Select Month">
                            {['April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December', 'January', 'February', 'March'].map(m => (
                              <Option key={m} value={m}>{m}</Option>
                            ))}
                          </Select>
                        </Form.Item>
                      );
                    }
                    return (
                      <Form.Item name="frequencyPeriod" label={<strong>Period Label / Notes</strong>}>
                        <Input placeholder="e.g. April 2026 - March 2027" />
                      </Form.Item>
                    );
                  }}
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="status"
                  label={<strong>Cycle Status</strong>}
                  rules={[{ required: true, message: 'Please select Cycle Status' }]}
                  tooltip="Auto mode dynamically calculates status based on today's date: Draft → Scheduled → Active → Completed"
                >
                  <Select>
                    <Option value="Auto">✨ Auto (Date-Driven)</Option>
                    <Option value="Draft">Draft</Option>
                    <Option value="Scheduled">Scheduled</Option>
                    <Option value="Active">Active</Option>
                    <Option value="Open">Open</Option>
                    <Option value="Self Assessment">Self Assessment</Option>
                    <Option value="Manager Review">Manager Review</Option>
                    <Option value="HR Review">HR Review</Option>
                    <Option value="Approval Pending">Approval Pending</Option>
                    <Option value="Completed">Completed</Option>
                    <Option value="Closed">Closed</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Card 2: Performance Evaluation Timeline */}
          <Card
            type="inner"
            title={
              <Space>
                <CalendarOutlined style={{ color: '#52c41a' }} />
                <span style={{ fontWeight: 600, color: '#334155' }}>Performance Period & Appraisal Start</span>
              </Space>
            }
            style={{ marginBottom: 16, borderRadius: 10, borderColor: '#e2e8f0', background: '#fafbfc' }}
            bodyStyle={{ padding: '16px 20px' }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="startDate"
                  label={<strong>Performance Period Start Date</strong>}
                  rules={[{ required: true, message: 'Please select Performance Period Start Date' }]}
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    format="DD MMM YYYY"
                    onChange={() => {
                      if (form.getFieldValue('endDate')) {
                        form.validateFields(['endDate']);
                      }
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="endDate"
                  label={<strong>Performance Period End Date</strong>}
                  dependencies={['startDate']}
                  rules={[
                    { required: true, message: 'Please select Performance Period End Date' },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const start = getFieldValue('startDate');
                        if (!value || !start) {
                          return Promise.resolve();
                        }
                        if (value.isAfter(start, 'day')) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error('Performance Period End Date must be after its Start Date'));
                      },
                    }),
                  ]}
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    format="DD MMM YYYY"
                    disabledDate={(current) => {
                      const start = form.getFieldValue('startDate');
                      return start ? current && current <= start.endOf('day') : false;
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="appraisalStartDate" label={<strong>Appraisal Process Start Date</strong>} style={{ marginBottom: 0 }}>
                  <DatePicker
                    style={{ width: '100%' }}
                    format="DD MMM YYYY"
                    onChange={() => {
                      if (form.getFieldValue('selfAppraisalDeadline')) {
                        form.validateFields(['selfAppraisalDeadline']);
                      }
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="selfAppraisalDeadline"
                  label={<strong>Self-Appraisal Deadline</strong>}
                  dependencies={['appraisalStartDate']}
                  style={{ marginBottom: 0 }}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const appraisalStart = getFieldValue('appraisalStartDate');
                        if (!value || !appraisalStart) {
                          return Promise.resolve();
                        }
                        if (value.isBefore(appraisalStart, 'day')) {
                          return Promise.reject(new Error('Self-Appraisal Deadline should not be before Appraisal Process Start Date'));
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    format="DD MMM YYYY"
                    disabledDate={(current) => {
                      const appraisalStart = form.getFieldValue('appraisalStartDate');
                      return appraisalStart ? current && current < appraisalStart.startOf('day') : false;
                    }}
                    onChange={() => {
                      if (form.getFieldValue('managerReviewDeadline')) {
                        form.validateFields(['managerReviewDeadline']);
                      }
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Card 3: Review Deadlines & Salary Revision */}
          <Card
            type="inner"
            title={
              <Space>
                <FieldTimeOutlined style={{ color: '#722ed1' }} />
                <span style={{ fontWeight: 600, color: '#334155' }}>Review Deadlines & Salary Revision</span>
              </Space>
            }
            style={{ marginBottom: 16, borderRadius: 10, borderColor: '#e2e8f0', background: '#fafbfc' }}
            bodyStyle={{ padding: '16px 20px' }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="managerReviewDeadline"
                  label={<strong>Manager Review Deadline</strong>}
                  dependencies={['selfAppraisalDeadline']}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const selfDeadline = getFieldValue('selfAppraisalDeadline');
                        if (!value || !selfDeadline) {
                          return Promise.resolve();
                        }
                        if (value.isBefore(selfDeadline, 'day')) {
                          return Promise.reject(new Error('Manager Review Deadline cannot be before Self-Appraisal Deadline'));
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    format="DD MMM YYYY"
                    placeholder="e.g. 31 Jan 2027"
                    disabledDate={(current) => {
                      const selfDeadline = form.getFieldValue('selfAppraisalDeadline');
                      return selfDeadline ? current && current < selfDeadline.startOf('day') : false;
                    }}
                    onChange={() => {
                      if (form.getFieldValue('hrReviewDeadline')) {
                        form.validateFields(['hrReviewDeadline']);
                      }
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="hrReviewDeadline"
                  label={<strong>HR Review Deadline</strong>}
                  dependencies={['managerReviewDeadline']}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const mgrDeadline = getFieldValue('managerReviewDeadline');
                        if (!value || !mgrDeadline) {
                          return Promise.resolve();
                        }
                        if (value.isBefore(mgrDeadline, 'day')) {
                          return Promise.reject(new Error('HR Review Deadline cannot be before Manager Review Deadline'));
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    format="DD MMM YYYY"
                    placeholder="e.g. 15 Feb 2027"
                    disabledDate={(current) => {
                      const mgrDeadline = form.getFieldValue('managerReviewDeadline');
                      return mgrDeadline ? current && current < mgrDeadline.startOf('day') : false;
                    }}
                    onChange={() => {
                      if (form.getFieldValue('managementApprovalDeadline')) {
                        form.validateFields(['managementApprovalDeadline']);
                      }
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="managementApprovalDeadline"
                  label={<strong>Management Approval Deadline</strong>}
                  dependencies={['hrReviewDeadline']}
                  style={{ marginBottom: 0 }}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const hrDeadline = getFieldValue('hrReviewDeadline');
                        if (!value || !hrDeadline) {
                          return Promise.resolve();
                        }
                        if (value.isBefore(hrDeadline, 'day')) {
                          return Promise.reject(new Error('Management Approval Deadline cannot be before HR Review Deadline'));
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    format="DD MMM YYYY"
                    placeholder="e.g. 28 Feb 2027"
                    disabledDate={(current) => {
                      const hrDeadline = form.getFieldValue('hrReviewDeadline');
                      return hrDeadline ? current && current < hrDeadline.startOf('day') : false;
                    }}
                    onChange={() => {
                      if (form.getFieldValue('salaryRevisionEffectiveDate')) {
                        form.validateFields(['salaryRevisionEffectiveDate']);
                      }
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="salaryRevisionEffectiveDate"
                  label={<strong>Salary Revision Effective Date</strong>}
                  dependencies={['managementApprovalDeadline']}
                  style={{ marginBottom: 0 }}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const mgmtDeadline = getFieldValue('managementApprovalDeadline');
                        if (!value || !mgmtDeadline) {
                          return Promise.resolve();
                        }
                        if (value.isBefore(mgmtDeadline, 'day')) {
                          return Promise.reject(new Error('Salary Revision Effective Date cannot be before Management Approval Deadline'));
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    format="DD MMM YYYY"
                    placeholder="e.g. 01 Apr 2027"
                    disabledDate={(current) => {
                      const mgmtDeadline = form.getFieldValue('managementApprovalDeadline');
                      return mgmtDeadline ? current && current < mgmtDeadline.startOf('day') : false;
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Card 4: Description & Policy Settings */}
          <Card
            type="inner"
            title={
              <Space>
                <FileTextOutlined style={{ color: '#fa8c16' }} />
                <span style={{ fontWeight: 600, color: '#334155' }}>Cycle Policy & Guidelines</span>
              </Space>
            }
            style={{ marginBottom: 16, borderRadius: 10, borderColor: '#e2e8f0', background: '#fafbfc' }}
            bodyStyle={{ padding: '16px 20px' }}
          >
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item name="description" label={<strong>Description / Guidelines</strong>}>
                  <Input.TextArea rows={2} placeholder="Optional instructions or notes for employees and managers in this cycle..." />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid #e2e8f0', background: '#fff', padding: '12px 16px', borderRadius: '8px' }}>
                  <div>
                    <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '13px' }}>
                      <SafetyCertificateOutlined style={{ color: '#1677ff', marginRight: 6 }} />
                      Allow HR Extension After Deadline
                    </div>
                    <div style={{ fontSize: '11px', color: '#64748b', marginTop: 2 }}>
                      Prevent users from taking actions after deadline unless HR explicitly enables an extension.
                    </div>
                  </div>
                  <Form.Item name="allowHrExtension" valuePropName="checked" noStyle>
                    <Switch />
                  </Form.Item>
                </div>
              </Col>
            </Row>
          </Card>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px' }}>
            <Button size="large" onClick={handleCloseModal}>Cancel</Button>
            <Button size="large" type="primary" htmlType="submit">
              {editingCycle ? 'Update Cycle' : 'Create Cycle'}
            </Button>
          </div>
        </Form>
      </Modal>
    </Layout>
  );
}
